<template>
  <div class="application-component tag-label-component">
    <div class="application-component-title">标签标注</div>
    <div
      class="application-component-content"
      :style="{ fontSize }"
      v-loading="loading"
      :element-loading-text="loadingText"
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="6" animated />
      </div>
      <template v-else>
        <div v-if="!hasAnnotationContent" class="dependency-tip">
          <div class="tip-title">标签标注依赖交底书正文内容</div>
          <div class="tip-desc">请先完成交底书正文解析，系统再生成标签建议。</div>
        </div>

        <template v-else>
          <div class="panel-head">
            <div>
              <div class="panel-title">标签建议</div>
              <div class="panel-meta">共 {{ items.length || 8 }} 个维度，修改后自动保存。</div>
            </div>
            <div class="panel-actions">
              <span v-if="resultData?.updatedAt" class="panel-updated">最近更新：{{ resultData.updatedAt }}</span>
              <el-button size="small" :loading="generating" @click="handleGenerate({ manual: true })">
                重新分析
              </el-button>
            </div>
          </div>

          <div v-if="sourceStale" class="stale-tip">
            <span>交底书正文或上游分析结果已变化，当前标签建议可能已过期。</span>
            <el-button size="small" text :loading="generating" @click="handleGenerate({ manual: true })">
              重新分析
            </el-button>
          </div>

          <div v-if="items.length" class="tag-table-scroll">
            <table class="tag-table">
              <thead>
                <tr>
                  <th class="dimension-column">标签维度</th>
                  <th class="type-column">标签类型</th>
                  <th class="source-column">来源</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in items" :key="item.dimensionCode">
                  <td class="dimension-column">
                    <div class="dimension-cell">
                      <div class="dimension-name">{{ item.dimensionLabel }}</div>
                    </div>
                  </td>
                  <td class="type-column">
                    <div class="cell-card">
                      <el-select
                        v-model="item.selectedValue"
                        class="type-select"
                        @change="value => handleSelectionChange(item, value)"
                      >
                        <el-option
                          v-for="option in item.options"
                          :key="`${item.dimensionCode}-${option.value}`"
                          :label="option.label"
                          :value="option.value"
                        />
                      </el-select>
                      <div v-if="item.reason" class="cell-reason textselection">
                        {{ item.reason }}
                      </div>
                    </div>
                  </td>
                  <td class="source-column">
                    <span :class="['source-pill', getSourceClass(item.source)]">
                      {{ item.source }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <el-empty v-else description="正在等待标签建议生成" />
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import { fetchGenerateTagLabel, fetchSaveComponent } from '@/api/writing'
import { jsonParse } from '@/utils'

const COMPONENT_TYPE_CODE = 'comp_tag'
const ANNOTATION_COMPONENT_CODE = 'comp_ann'
const FEATURE_BREAKDOWN_COMPONENT_CODE = 'comp_fea'
const NOVELTY_COMPONENT_CODE = 'comp_nov'
const CREATIVITY_COMPONENT_CODE = 'comp_cre'
const COMPLETENESS_COMPONENT_CODE = 'comp_cmp'
const POPULAR_COMPONENT_CODE = 'comp_poe'
const INVENTIVE_COMPONENT_CODE = 'comp_inv'

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  }
})

const resultData = ref(null)
const generating = ref(false)
const saving = ref(false)
const hasAutoInitialized = ref(false)
const localExtOne = ref('')

const fontSize = computed(() => `${globalStore.fontSize}px`)
const loading = computed(() => generating.value || saving.value)
const loadingText = computed(() => {
  if (generating.value) {
    return '正在思考中...'
  }
  if (saving.value) {
    return '正在思考中...'
  }
  return ''
})

const annotationRaw = computed(() => writingStore.step2Info?.step2Component?.[ANNOTATION_COMPONENT_CODE]?.docResult || '')
const featureRaw = computed(() => writingStore.step2Info?.step2Component?.[FEATURE_BREAKDOWN_COMPONENT_CODE]?.docResult || '')
const noveltyRaw = computed(() => writingStore.step2Info?.step2Component?.[NOVELTY_COMPONENT_CODE]?.docResult || '')
const creativityRaw = computed(() => writingStore.step2Info?.step2Component?.[CREATIVITY_COMPONENT_CODE]?.docResult || '')
const completenessRaw = computed(() => writingStore.step2Info?.step2Component?.[COMPLETENESS_COMPONENT_CODE]?.docResult || '')
const popularRaw = computed(() => writingStore.step2Info?.step2Component?.[POPULAR_COMPONENT_CODE]?.docResult || '')
const inventiveRaw = computed(() => writingStore.step2Info?.step2Component?.[INVENTIVE_COMPONENT_CODE]?.docResult || '')

const annotationItems = computed(() => {
  const parsed = jsonParse(annotationRaw.value, [])
  if (!Array.isArray(parsed)) {
    return []
  }
  return parsed.filter(item => item?.type === 'TEXT' && String(item?.text || '').trim())
})

const normalizeParagraphText = (value = '') => {
  const normalized = String(value || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  if (/^PAGE\s+\d+$/i.test(normalized.replace(/\s+/g, ' '))) {
    return ''
  }

  return normalized
}

const buildParagraphSnapshot = (items = []) => {
  const lines = []
  let fallbackIndex = 0

  items.forEach((item) => {
    const text = normalizeParagraphText(item?.text)
    if (!text) {
      return
    }

    const paragraphIndex = Number.isInteger(item?.index) ? item.index : fallbackIndex
    lines.push(`[${paragraphIndex}] ${text}`)
    fallbackIndex += 1
  })

  return lines.length ? `${lines.join('\n')}\n` : ''
}

const hasAnnotationContent = computed(() => annotationItems.value.length > 0)
const items = computed(() => resultData.value?.items || [])
const paragraphSnapshot = computed(() => buildParagraphSnapshot(annotationItems.value))
const currentSourceVersion = computed(() => hashJavaString([
  paragraphSnapshot.value,
  featureRaw.value,
  noveltyRaw.value,
  creativityRaw.value,
  completenessRaw.value,
  popularRaw.value,
  inventiveRaw.value
].join('||')))
const sourceStale = computed(() => {
  if (!resultData.value?.sourceVersion || !currentSourceVersion.value) {
    return false
  }
  return resultData.value.sourceVersion !== currentSourceVersion.value
})

const hashJavaString = (value = '') => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) | 0
  }
  return (hash >>> 0).toString(16)
}

const formatNow = () => {
  const now = new Date()
  const pad = (value) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

const normalizeOption = (item = {}, index = 0) => {
  return {
    value: item?.value || `option-${index}`,
    label: item?.label || item?.value || ''
  }
}

const INVENTION_TYPE_DIMENSION_CODE = 'INVENTION_TYPE'
const INVENTION_TYPE_OPTIONS = [
  { value: 'INVENTION', label: '发明' },
  { value: 'UTILITY_MODEL', label: '实用新型' },
  { value: 'DESIGN', label: '外观' }
]
const INVENTION_TYPE_SELECTION_MAP = {
  INVENTION: INVENTION_TYPE_OPTIONS[0],
  '发明': INVENTION_TYPE_OPTIONS[0],
  METHOD: INVENTION_TYPE_OPTIONS[0],
  PROCESS: INVENTION_TYPE_OPTIONS[0],
  SYSTEM: INVENTION_TYPE_OPTIONS[0],
  '方法': INVENTION_TYPE_OPTIONS[0],
  '流程': INVENTION_TYPE_OPTIONS[0],
  '工艺': INVENTION_TYPE_OPTIONS[0],
  '系统': INVENTION_TYPE_OPTIONS[0],
  UTILITYMODEL: INVENTION_TYPE_OPTIONS[1],
  UTILITY_MODEL: INVENTION_TYPE_OPTIONS[1],
  DEVICE: INVENTION_TYPE_OPTIONS[1],
  '实用新型': INVENTION_TYPE_OPTIONS[1],
  '装置': INVENTION_TYPE_OPTIONS[1],
  '设备': INVENTION_TYPE_OPTIONS[1],
  '器件': INVENTION_TYPE_OPTIONS[1],
  DESIGN: INVENTION_TYPE_OPTIONS[2],
  '外观': INVENTION_TYPE_OPTIONS[2],
  '外观设计': INVENTION_TYPE_OPTIONS[2]
}

const normalizeInventionTypeKey = (value = '') => String(value || '').trim().replace(/\s+/g, '').toUpperCase()

const normalizeInventionTypeSelection = (item = {}) => {
  const candidates = [item?.selectedValue, item?.selectedLabel]
  for (const candidate of candidates) {
    const rawValue = String(candidate || '').trim()
    const matched = INVENTION_TYPE_SELECTION_MAP[normalizeInventionTypeKey(candidate)]
      || INVENTION_TYPE_SELECTION_MAP[rawValue]
    if (matched) {
      return {
        selectedValue: matched.value,
        selectedLabel: matched.label
      }
    }

    if (rawValue.includes('实用新型')) {
      return {
        selectedValue: INVENTION_TYPE_OPTIONS[1].value,
        selectedLabel: INVENTION_TYPE_OPTIONS[1].label
      }
    }
    if (rawValue.includes('外观')) {
      return {
        selectedValue: INVENTION_TYPE_OPTIONS[2].value,
        selectedLabel: INVENTION_TYPE_OPTIONS[2].label
      }
    }
    if (rawValue.includes('发明')) {
      return {
        selectedValue: INVENTION_TYPE_OPTIONS[0].value,
        selectedLabel: INVENTION_TYPE_OPTIONS[0].label
      }
    }
  }

  return {
    selectedValue: '',
    selectedLabel: ''
  }
}

const ensureSelectedOption = (item) => {
  const nextOptions = Array.isArray(item?.options) ? [...item.options] : []
  const hasSelected = nextOptions.some(option => option.value === item.selectedValue)
  if (!hasSelected && (item.selectedLabel || item.selectedValue)) {
    nextOptions.push({
      value: item.selectedValue || item.selectedLabel,
      label: item.selectedLabel || item.selectedValue
    })
  }
  return nextOptions
}

const normalizeItem = (item = {}, index = 0) => {
  const nextItem = {
    dimensionCode: item?.dimensionCode || `dimension-${index}`,
    dimensionLabel: item?.dimensionLabel || `标签维度 ${index + 1}`,
    source: item?.source || 'AI识别',
    selectedValue: item?.selectedValue || '',
    selectedLabel: item?.selectedLabel || '',
    reason: item?.reason || '',
    sort: Number(item?.sort ?? index + 1),
    options: Array.isArray(item?.options) ? item.options.map(normalizeOption) : []
  }

  if (nextItem.dimensionCode === INVENTION_TYPE_DIMENSION_CODE) {
    const normalizedSelection = normalizeInventionTypeSelection(nextItem)
    nextItem.selectedValue = normalizedSelection.selectedValue
    nextItem.selectedLabel = normalizedSelection.selectedLabel
    nextItem.options = INVENTION_TYPE_OPTIONS.map(option => ({ ...option }))
  }

  nextItem.options = ensureSelectedOption(nextItem)
  return nextItem
}

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  const itemList = Array.isArray(parsed.items)
    ? parsed.items.map(normalizeItem).sort((a, b) => a.sort - b.sort)
    : []

  return {
    sourceVersion: parsed.sourceVersion || '',
    updatedAt: parsed.updatedAt || '',
    itemCount: Number(parsed.itemCount ?? itemList.length),
    items: itemList
  }
}

const syncStepState = (nextResult, extOne = localExtOne.value) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized) {
    return
  }

  resultData.value = normalized
  writingStore.setStep2Info({
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [COMPONENT_TYPE_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE] || {}),
        ...(props.data || {}),
        docResult: JSON.stringify(normalized),
        extOne,
      }
    }
  })
}

const persistResult = async (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized || !props.projectId) {
    return
  }

  normalized.itemCount = normalized.items.length
  normalized.updatedAt = formatNow()
  normalized.sourceVersion = normalized.sourceVersion || currentSourceVersion.value

  saving.value = true
  try {
    await fetchSaveComponent({
      disclosureProjectId: props.projectId,
      componentTypeCode: COMPONENT_TYPE_CODE,
      docResult: JSON.stringify(normalized),
      extOne: localExtOne.value || props.data?.extOne || '',
    })
    syncStepState(normalized, localExtOne.value || props.data?.extOne || '')
  } finally {
    saving.value = false
  }
}

const handleGenerate = async ({ manual = false } = {}) => {
  if (!props.projectId || generating.value || !hasAnnotationContent.value) {
    return
  }

  generating.value = true
  try {
    const result = await fetchGenerateTagLabel(props.projectId)
    syncStepState(result, localExtOne.value || props.data?.extOne || '')
    if (manual) {
      ElMessage.success('标签建议已更新')
    }
  } catch (error) {
    console.error('generate tag label failed', error)
    ElMessage.error(manual ? '标签建议生成失败' : '标签建议初始化失败')
  } finally {
    generating.value = false
  }
}

const handleSelectionChange = async (item, value) => {
  if (!resultData.value) {
    return
  }

  const matched = (item.options || []).find(option => option.value === value)
  item.selectedValue = value
  item.selectedLabel = matched?.label || item.selectedLabel || value

  try {
    await persistResult({
      ...resultData.value,
      items: items.value
    })
  } catch (error) {
    console.error('save tag label failed', error)
    ElMessage.error('标签标注保存失败')
  }
}

const getSourceClass = (source = '') => {
  if (source.includes('系统')) {
    return 'is-system'
  }
  if (source.includes('AI')) {
    return 'is-ai'
  }
  return 'is-module'
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    resultData.value = normalizeResult(newValue)
  },
  { immediate: true }
)

watch(
  () => props.data?.extOne,
  (newValue) => {
    localExtOne.value = newValue || ''
  },
  { immediate: true }
)

watch(
  () => props.projectId,
  () => {
    hasAutoInitialized.value = false
  },
  { immediate: true }
)

watch(
  () => currentSourceVersion.value,
  async (newValue) => {
    if (!newValue || props.readOnly || resultData.value || hasAutoInitialized.value || !hasAnnotationContent.value) {
      return
    }

    hasAutoInitialized.value = true
    await handleGenerate()
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
@import './common.scss';

.dependency-tip,
.stale-tip,
.panel-head {
  padding: 14px 16px;
  border-radius: 14px;
}

.dependency-tip {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #dbe4ee;
}

.tip-title,
.panel-title {
  color: #111827;
  font-weight: 600;
}

.tip-desc,
.panel-meta {
  margin-top: 6px;
  color: #4b5563;
  line-height: 1.7;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-updated {
  color: #6b7280;
  font-size: 12px;
}

.stale-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fdba74;
}

.tag-table-scroll {
  margin-top: 16px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.tag-table {
  width: 100%;
  min-width: 860px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    padding: 14px 12px;
    vertical-align: top;
    border-bottom: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    color: #111827;
    font-weight: 600;
    background: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.dimension-column {
  width: 28%;
}

.type-column {
  width: 50%;
}

.source-column {
  width: 22%;
}

.dimension-cell,
.cell-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dimension-name {
  color: #111827;
  font-weight: 600;
  line-height: 1.6;
}

.type-select {
  width: 100%;
}

.cell-reason {
  color: #6b7280;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.source-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;

  &.is-ai {
    color: #14532d;
    background: #dcfce7;
  }

  &.is-module {
    color: #1d4ed8;
    background: #dbeafe;
  }

  &.is-system {
    color: #7c2d12;
    background: #ffedd5;
  }
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

:deep(.type-select .el-select__wrapper),
:deep(.type-select .el-select__selected-item),
:deep(.type-select .el-select__placeholder) {
  font-size: inherit;
}

@media (max-width: 960px) {
  .panel-head,
  .stale-tip {
    flex-direction: column;
    align-items: stretch;
  }

  .panel-actions {
    justify-content: space-between;
  }
}
</style>
